import { AddCircleOutlined, AddOutlined, Delete } from "@mui/icons-material";
import { Button } from "@mui/material";
import {
  MRT_ColumnDef,
  MRT_Row,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useAuthGroupsQuery,
  useDeleteAuthGroupsMutation,
} from "@/api/hooks/auth-groups-hooks";
import CreateAuthGroupModal from "@/components/create-auth-group-modal";
import { useMuiTableToolbarAlertBannerProps } from "@/hooks/use-mui-table-toolbar-alert-banner-props";
import { useTableSettings } from "@/hooks/use-table-settings";
import { COMMON_REFETCH_INTERVAL } from "@/lib/constants";
import { AuthGroupRegularResponseDto } from "@/shared/api";

const AuthGroupsModule = () => {
  const {
    data: authGroups,
    isLoading: authGroupsIsLoading,
    isError: authGroupsIsError,
    error: authGroupsError,
    isRefetching: authGroupsIsRefetching,
    refetch: refetchAuthGroups,
  } = useAuthGroupsQuery({
    refetchInterval: COMMON_REFETCH_INTERVAL,
  });
  const { t } = useTranslation();

  const [createNew, setCreateNew] = useState<boolean>(false);
  const deleteAuthGroups = useDeleteAuthGroupsMutation();

  function handleDeleteAuthGroups(rows: MRT_Row<AuthGroupRegularResponseDto>[]) {
    deleteAuthGroups.mutate(
      {
        groups: rows.map((row) => row.original.name),
      },
      {
        onSuccess: (response) => {
          refetchAuthGroups().then();
          enqueueSnackbar(t("authGroupsDataSuccessfullyDeleted", { data: response.data }), {
            variant: "success",
          });
        },
        onError: (err) => {
          enqueueSnackbar(t("somethingWentWrongDuringDeletingAuthGroups"), {
            variant: "error",
          });
          console.error("deleting auth groups failure", err);
        },
      }
    );
  }

  const columns = useMemo<MRT_ColumnDef<AuthGroupRegularResponseDto>[]>(
    () => [
      {
        header: t("name"),
        accessorKey: "name",
      },
    ],
    []
  );

  const { defaultProps, state } = useTableSettings<AuthGroupRegularResponseDto>("authGroups");
  const muiToolbarAlertBannerProps = useMuiTableToolbarAlertBannerProps(
    Boolean(authGroupsIsError),
    authGroupsError?.message
  );

  const table = useMaterialReactTable({
    data: authGroups?.data ?? [],
    columns: columns,
    getRowId: (originalRow) => originalRow.name ?? "",
    selectAllMode: "all",
    ...defaultProps,
    enableGrouping: true,
    enableColumnDragging: false,
    enableColumnFilterModes: true,
    enablePagination: true,
    enableSorting: true,
    enableSelectAll: true,
    enableRowSelection: true,
    muiToolbarAlertBannerProps,
    state: {
      ...state,
      isLoading: authGroupsIsLoading,
      showAlertBanner: authGroupsIsError,
      showProgressBars: authGroupsIsRefetching,
    },
    renderTopToolbarCustomActions: ({ table }) => {
      const rows = table.getSelectedRowModel().rows;
      return (
        <div>
          <Button
            variant="text"
            onClick={() => setCreateNew(true)}
            //startIcon={<AddCircleOutlined />}
            startIcon={<AddOutlined />}
          >
            {t("new")}
          </Button>
          {rows.length > 0 && (
            <Button
              startIcon={<Delete />}
              color="error"
              onClick={() => handleDeleteAuthGroups(rows)}
            >
              {t("deleteLength", { length: rows.length })}
            </Button>
          )}
        </div>
      );
    },
  });

  return (
    <>
      <CreateAuthGroupModal
        onClose={() => setCreateNew(false)}
        onSuccess={() => refetchAuthGroups()}
        open={createNew}
      />
      <MaterialReactTable table={table} />
    </>
  );
};

export default AuthGroupsModule;
